import uniq from 'lodash/uniq'
import {exhaustive} from '@freckle/exhaustive'
import type {ParserT} from '@freckle/parser'
import {Parser, record, string, array, number, nullable, field} from '@freckle/parser'
import type {TaggedSavedAnswerT} from '@freckle/educator-entities/ts/math/common/logic/answer-types'
import {parseTaggedSavedAnswer} from '@freckle/educator-entities/ts/math/common/logic/answer-types'
import {fancyClient, extractData} from '@freckle/educator-entities/ts/openapi-clients'

export type AssessmentAnswerT = {
  id: number
  updatedAt: string
  correctness: number | undefined | null
  answer: TaggedSavedAnswerT | undefined | null
  questionId: string
  asmtSessId: number
  durationSeconds: number | undefined | null
  standard: {id: string}
}

const assessmentAnswerParser: ParserT<AssessmentAnswerT> = record({
  id: number(),
  updatedAt: string(),
  correctness: nullable(number()),
  answer: nullable(parseTaggedSavedAnswer),
  questionId: field(string(), 'mathQuestionId'),
  asmtSessId: field(number(), 'assessmentSessionId'),
  durationSeconds: nullable(number()),
  standard: record({id: string()}),
})

export const parseAssessmentAnswer = Parser.mkRun<AssessmentAnswerT>(assessmentAnswerParser)

type AssessmentAnswersForT =
  | {
      tag: 'session'
      id: number
    }
  | {
      tag: 'assignment'
      id: number
    }
  | {
      tag: 'school'
      id: number
      assignmentId: number
    }

export async function fetchAssessmentAnswers(
  answersFor: AssessmentAnswersForT,
): Promise<Array<AssessmentAnswerT>> {
  let response: Array<unknown>

  switch (answersFor.tag) {
    case 'session':
      response = await fancyClient
        .GET(
          '/2/math-assessment-sessions/{MathAssessmentSessionsAssessmentAnswersR-MathAssessmentAssignmentSessionId}/assessment-answers',
          {
            params: {
              path: {
                'MathAssessmentSessionsAssessmentAnswersR-MathAssessmentAssignmentSessionId':
                  answersFor.id,
              },
            },
          },
        )
        .then(extractData)
      break
    case 'assignment':
      response = await fancyClient
        .GET(
          '/2/math-assessments/{MathAssessmentsAssessmentAnswersR-MathAssessmentAssignmentId}/assessment-answers',
          {
            params: {
              path: {
                'MathAssessmentsAssessmentAnswersR-MathAssessmentAssignmentId': answersFor.id,
              },
            },
          },
        )
        .then(extractData)
      break
    case 'school':
      response = await fancyClient
        .GET(
          '/2/schools/{SchoolP-SchoolId}/math/assessments/assignments/{SchoolMathAssessmentsAssignmentP-MathAssessmentAssignmentId}/answers',
          {
            params: {
              path: {
                'SchoolP-SchoolId': answersFor.id,
                'SchoolMathAssessmentsAssignmentP-MathAssessmentAssignmentId':
                  answersFor.assignmentId,
              },
            },
          },
        )
        .then(extractData)
      break
    default:
      return exhaustive(answersFor)
  }

  return Parser.run(response, array(assessmentAnswerParser))
}

export function getQuestionIdsOfAssessment(answers: Array<AssessmentAnswerT>): Array<string> {
  const allQuestions = answers.map(answer => answer.questionId)
  return uniq(allQuestions)
}
